import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Progress } from "../../ui/progress";
import { Alert, AlertDescription } from "../../ui/alert";
import {
  Upload,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  FileImage,
  Shield,
  Camera,
  Monitor,
  FileText,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { createWorker } from "tesseract.js";
import { formatICDCodes, cleanText, extractMedications, extractMedicationDetails } from "../../../utils/ocrUtils";
import { prechartingService } from "../../../apis/prechartingService";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Pill } from "lucide-react";

const OCRModal = ({ isOpen, onClose, onExtractComplete, setShowOCRModal, mode = 'icd10' }) => {
  const [isHidden, setIsHidden] = useState(false);
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [extractedCodes, setExtractedCodes] = useState([]);
  const [extractedMedications, setExtractedMedications] = useState([]);
  const [activeTab, setActiveTab] = useState(mode === 'medication' ? 'medication' : 'icd10');
  const [isComplete, setIsComplete] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showWindowSelection, setShowWindowSelection] = useState(false);
  const [isSelectingRegion, setIsSelectingRegion] = useState(false);
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const selectionRef = useRef({ startX: 0, startY: 0, endX: 0, endY: 0 });

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const SUPPORTED_FORMATS = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/tiff",
    "image/tif",
    "image/bmp",
    "image/webp",
    "image/gif",
  ];

  const handleFileSelect = useCallback((selectedFiles) => {
    const fileArray = Array.from(selectedFiles || []);
    const validFiles = [];

    for (const file of fileArray) {
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        toast.error(`${file.name}: Only image files are allowed`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: File must be under 20MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
      setExtractedCodes([]);
      setExtractedMedications([]);
      setIsComplete(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleFileSelect(e.dataTransfer?.files);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setExtractedCodes([]);
    setExtractedMedications([]);
    setIsComplete(false);
  };

  const processImage = async (file, index, total) => {
    try {
      setStatus(`Processing ${file.name} (${index + 1}/${total})...`);
      const fileStartProgress = (index / total) * 100;
      const fileEndProgress = ((index + 1) / total) * 100;
      setProgress(fileStartProgress);

      // Initialize worker if not already done (without logger to avoid cloning issues)
      if (!workerRef.current) {
        workerRef.current = await createWorker("eng");
      }

      // Simulate progress during recognition (since logger can't be cloned)
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          // Gradually increase progress while processing this file
          const increment = (fileEndProgress - fileStartProgress) / 20; // 20 steps
          if (prev < fileEndProgress - increment) {
            return Math.min(prev + increment, fileEndProgress - increment);
          }
          return prev;
        });
      }, 150);

      // Perform OCR recognition
      const {
        data: { text, confidence },
      } = await workerRef.current.recognize(file);

      clearInterval(progressInterval);
      setProgress(fileEndProgress);

      return { text, confidence, fileName: file.name };
    } catch (error) {
      console.error(`Error processing ${file.name}:`, error);
      throw error;
    }
  };

  const handleExtract = async () => {
    if (files.length === 0) {
      toast.error("Please select at least one image file");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setStatus("Initializing OCR engine...");
    setExtractedCodes([]);
    setExtractedMedications([]);
    setIsComplete(false);

    try {
      const allTexts = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const result = await processImage(file, i, files.length);
        allTexts.push(result.text);
      }

      if (activeTab === 'icd10') {
        await handleExtractICD10Codes(allTexts);
      } else {
        await handleExtractMedications(allTexts);
      }
    } catch (error) {
      console.error("Error during OCR extraction:", error);
      toast.error(`Failed to extract ${activeTab === 'icd10' ? 'codes' : 'medications'}. Please try again.`);
      setStatus("Error occurred during extraction");
    } finally {
      setIsProcessing(false);
      if (workerRef.current) {
        await workerRef.current.terminate();
        workerRef.current = null;
      }
    }
  };

  const handleExtractICD10Codes = async (allTexts) => {
    setStatus("Extracting ICD-10 codes from text...");
    setProgress(50);

    // Process all extracted text
    const allRawCodes = [];
    for (const text of allTexts) {
      const cleanedText = cleanText(text);
      const codes = formatICDCodes(cleanedText);
      allRawCodes.push(...codes);
    }

    // Remove duplicates by code
    const uniqueCodeSet = new Set();
    const uniqueCodes = [];
    for (const code of allRawCodes) {
      if (!uniqueCodeSet.has(code.code)) {
        uniqueCodeSet.add(code.code);
        uniqueCodes.push(code);
      }
    }

    uniqueCodes.sort((a, b) => a.code.localeCompare(b.code));

    setStatus("Validating codes against ICD-10 database...");
    setProgress(70);

    // Validate and fetch descriptions from database
    const validatedCodes = await Promise.all(
      uniqueCodes.map(async (codeObj) => {
        try {
          const result = await prechartingService.lookupICD10Code(
            codeObj.code
          );
          if (result.success && result.data) {
            return {
              code: codeObj.code,
              description:
                result.data.description ||
                result.data.fullDescription ||
                "Description not available",
              hcc: codeObj.hcc || false,
              isValidated: true,
              isValid: true,
            };
          } else {
            return {
              code: codeObj.code,
              description: "Code not found in database",
              hcc: codeObj.hcc || false,
              isValidated: true,
              isValid: false,
            };
          }
        } catch (error) {
          console.error(`Error validating code ${codeObj.code}:`, error);
          return {
            code: codeObj.code,
            description: "Validation error",
            hcc: codeObj.hcc || false,
            isValidated: false,
            isValid: false,
          };
        }
      })
    );

    setExtractedCodes(validatedCodes);
    setProgress(100);
    setStatus("Extraction complete!");
    setIsComplete(true);

    if (validatedCodes.length > 0) {
      const validCount = validatedCodes.filter((c) => c.isValid).length;
      toast.success(
        `Successfully extracted ${validatedCodes.length} ICD-10 codes (${validCount} validated).`
      );
    } else {
      toast.success("Extraction complete, but no ICD-10 codes found.");
    }
  };

  const handleExtractMedications = async (allTexts) => {
    setStatus("Extracting medications from text...");
    setProgress(50);

    // Process all extracted text to get structured medication details
    const allMedicationDetails = [];
    for (const text of allTexts) {
      const cleanedText = cleanText(text);
      const medications = extractMedicationDetails(cleanedText);
      allMedicationDetails.push(...medications);
    }

    // Remove duplicates based on medication name (case-insensitive)
    const uniqueMedicationsMap = new Map();
    for (const med of allMedicationDetails) {
      const key = med.name.toLowerCase();
      if (!uniqueMedicationsMap.has(key)) {
        uniqueMedicationsMap.set(key, med);
      } else {
        // Merge details if duplicate found (prefer more complete information)
        const existing = uniqueMedicationsMap.get(key);
        if (!existing.dosage && med.dosage) existing.dosage = med.dosage;
        if (!existing.form && med.form) existing.form = med.form;
        if (!existing.frequency && med.frequency) existing.frequency = med.frequency;
        if (!existing.route && med.route) existing.route = med.route;
        if (!existing.instructions && med.instructions) existing.instructions = med.instructions;
      }
    }

    const uniqueMedications = Array.from(uniqueMedicationsMap.values());
    uniqueMedications.sort((a, b) => a.name.localeCompare(b.name));

    setStatus("Validating medications against database...");
    setProgress(70);

    // Validate medications against database
    const validatedMedications = await Promise.all(
      uniqueMedications.map(async (medication) => {
        try {
          const result = await prechartingService.lookupMedication(medication.name);
          if (result.success && result.data) {
            return {
              medicationName: result.data.medicationName,
              companyName: result.data.companyName || '',
              medicationType: result.data.medicationType || '',
              preventive: result.data.preventive || false,
              isValidated: true,
              isValid: true,
              // Include extracted details
              dosage: medication.dosage || '',
              form: medication.form || '',
              quantity: medication.quantity || '',
              route: medication.route || '',
              frequency: medication.frequency || '',
              instructions: medication.instructions || '',
              notes: medication.notes || '',
              status: medication.status || 'taking'
            };
          } else {
            return {
              medicationName: medication.name,
              companyName: '',
              medicationType: '',
              preventive: false,
              isValidated: true,
              isValid: false,
              // Include extracted details even if not validated
              dosage: medication.dosage || '',
              form: medication.form || '',
              quantity: medication.quantity || '',
              route: medication.route || '',
              frequency: medication.frequency || '',
              instructions: medication.instructions || '',
              notes: medication.notes || '',
              status: medication.status || 'taking'
            };
          }
        } catch (error) {
          console.error(`Error validating medication ${medication.name}:`, error);
          return {
            medicationName: medication.name,
            companyName: '',
            medicationType: '',
            preventive: false,
            isValidated: false,
            isValid: false,
            // Include extracted details
            dosage: medication.dosage || '',
            form: medication.form || '',
            quantity: medication.quantity || '',
            route: medication.route || '',
            frequency: medication.frequency || '',
            instructions: medication.instructions || '',
            notes: medication.notes || '',
            status: medication.status || 'taking'
          };
        }
      })
    );

    // Include all medications (both validated and unvalidated)
    const allMedications = validatedMedications;
    const validCount = allMedications.filter(m => m.isValidated && m.isValid).length;
    const unvalidatedCount = allMedications.filter(m => !m.isValidated || !m.isValid).length;
    
    setExtractedMedications(allMedications);
    setProgress(100);
    setStatus("Extraction complete!");
    setIsComplete(true);

    if (allMedications.length > 0) {
      if (validCount > 0 && unvalidatedCount > 0) {
        toast.success(
          `Successfully extracted ${validCount} validated and ${unvalidatedCount} unverified medications with details.`
        );
      } else if (validCount > 0) {
        toast.success(
          `Successfully extracted ${validCount} validated medications with details.`
        );
      } else {
        toast.success(
          `Extracted ${unvalidatedCount} unverified medications. Please review and validate them.`
        );
      }
    } else {
      toast.success("Extraction complete, but no medications found.");
    }
  };

  // Screenshot capture functionality

  const handleScreenshotCapture = async () => {
    let cleanup = null;

    const preventEvent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    try {
      setIsCapturing(true);

      // 1. SAFE HIDE: Use React state instead of DOM manipulation
      setIsHidden(true); 

      // 2. Global Shield
      window.addEventListener('click', preventEvent, { capture: true });

      // 3. Start Stream
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: 'screen' },
        audio: false
      });

      // 4. Video Setup
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      video.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
        object-fit: contain; background: black; z-index: 2147483646; 
      `;
      document.body.appendChild(video);

      await new Promise((resolve) => {
        video.onloadedmetadata = () => { video.play().then(resolve); };
      });

      // 5. Overlay
      const overlay = document.createElement('div');
      overlay.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
        background: transparent; z-index: 2147483647; cursor: crosshair; touch-action: none;
      `;

      // 6. UI Controls
      const controls = document.createElement('div');
      controls.style.cssText = `
        position: absolute; top: 20px; left: 50%; transform: translateX(-50%);
        display: flex; gap: 10px; align-items: center; pointer-events: auto;
      `;

      // Instruction Text
      const instruction = document.createElement('div');
      instruction.innerHTML = `
        <span style="font-weight:600">Live Preview</span><br>
        <span style="font-size:13px; opacity:0.9">Drag to Select</span>
      `;
      instruction.style.cssText = `
        background: rgba(0, 0, 0, 0.85); color: white; padding: 10px 20px;
        border-radius: 30px; font-size: 13px; text-align: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3); pointer-events: none;
      `;

      // Retake Button
      const retakeBtn = document.createElement('button');
      retakeBtn.innerText = "↺ Unfreeze";
      retakeBtn.style.cssText = `
        background: #3b82f6; color: white; border: none; padding: 8px 16px;
        border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer; display: none;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;

      // Confirm Button
      const confirmBtn = document.createElement('button');
      confirmBtn.innerHTML = "✓ Confirm";
      confirmBtn.style.cssText = `
        background: #22c55e; color: white; border: none; padding: 8px 16px;
        border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer; display: none;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;

      controls.appendChild(retakeBtn);
      controls.appendChild(instruction);
      controls.appendChild(confirmBtn);
      overlay.appendChild(controls);

      // Selection Box
      const selectionBox = document.createElement('div');
      selectionBox.style.cssText = `
        position: absolute; border: 2px dashed #60a5fa;
        background: rgba(59, 130, 246, 0.2); display: none; pointer-events: none;
      `;
      overlay.appendChild(selectionBox);

      // Frozen Image
      const frozenImg = document.createElement('img');
      frozenImg.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        object-fit: contain; display: none; z-index: 2147483645; pointer-events: none;
      `;
      document.body.appendChild(frozenImg);
      document.body.appendChild(overlay);

      // State
      let isSelecting = false;
      let isFrozen = false;
      let startX = 0, startY = 0;
      let currentSelection = null;
      let fullResCanvas = null;

      const updateUI = (state) => {
        if (state === 'live') {
          instruction.innerHTML = `<span style="font-weight:600">Live Preview</span><br><span style="font-size:13px">Scroll & Drag to Freeze</span>`;
          retakeBtn.style.display = 'none';
          confirmBtn.style.display = 'none';
          instruction.style.display = 'block';
        } else if (state === 'frozen') {
          instruction.innerHTML = `<span style="font-weight:600">Image Frozen</span><br><span style="font-size:13px">Drag to Select</span>`;
          retakeBtn.style.display = 'block';
          confirmBtn.style.display = 'none';
          instruction.style.display = 'block';
        } else if (state === 'selected') {
          instruction.style.display = 'none';
          retakeBtn.style.display = 'block';
          confirmBtn.style.display = 'block';
        }
      };
      updateUI('live');

      // --- BUTTON HANDLERS ---
      
      retakeBtn.onpointerdown = (e) => {
        e.stopPropagation(); e.preventDefault();
        
        isFrozen = false;
        isSelecting = false;
        currentSelection = null;
        selectionBox.style.display = 'none';
        
        frozenImg.style.display = 'none';
        video.style.display = 'block';
        video.play();
        
        updateUI('live');
      };

      confirmBtn.onpointerdown = (e) => {
        e.stopPropagation(); e.preventDefault();
        if (!currentSelection) return;

        try {
          const rect = frozenImg.getBoundingClientRect();
          const scaleX = frozenImg.naturalWidth / rect.width;
          const scaleY = frozenImg.naturalHeight / rect.height;

          const finalX = (currentSelection.x - rect.left) * scaleX;
          const finalY = (currentSelection.y - rect.top) * scaleY;
          const finalW = currentSelection.w * scaleX;
          const finalH = currentSelection.h * scaleY;

          const safeX = Math.max(0, finalX);
          const safeY = Math.max(0, finalY);
          const safeW = Math.min(finalW, frozenImg.naturalWidth - safeX);
          const safeH = Math.min(finalH, frozenImg.naturalHeight - safeY);

          const cropCanvas = document.createElement('canvas');
          cropCanvas.width = safeW;
          cropCanvas.height = safeH;
          const cropCtx = cropCanvas.getContext('2d');
          cropCtx.drawImage(fullResCanvas, safeX, safeY, safeW, safeH, 0, 0, safeW, safeH);

          cropCanvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
              
              handleFileSelect([file]);
              toast.success('Screenshot attached');
              
              cleanupElements();
              setTimeout(() => {
                window.removeEventListener('click', preventEvent, { capture: true });
                setIsCapturing(false);
                setIsHidden(false);
              }, 500);
            }
          }, 'image/png');
        } catch (err) {
          console.error(err);
          cleanup();
        }
        setTimeout(() => {
          setShowOCRModal(true);
        }, 400);
      };

      // --- POINTER HANDLERS ---
      
      const handlePointerDown = (e) => {
        if (controls.contains(e.target)) return;
        e.preventDefault(); e.stopPropagation();

        if (!isFrozen) {
          isFrozen = true;
          fullResCanvas = document.createElement('canvas');
          fullResCanvas.width = video.videoWidth;
          fullResCanvas.height = video.videoHeight;
          const ctx = fullResCanvas.getContext('2d');
          ctx.drawImage(video, 0, 0);

          frozenImg.src = fullResCanvas.toDataURL('image/png');
          frozenImg.style.display = 'block';
          video.style.display = 'none';
        }

        isSelecting = true;
        startX = e.clientX;
        startY = e.clientY;
        
        confirmBtn.style.display = 'none';
        updateUI('frozen');

        selectionBox.style.display = 'block';
        selectionBox.style.left = `${startX}px`;
        selectionBox.style.top = `${startY}px`;
        selectionBox.style.width = '0px';
        selectionBox.style.height = '0px';
        
        overlay.setPointerCapture(e.pointerId);
      };

      const handlePointerMove = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!isSelecting) return;

        const currentX = e.clientX;
        const currentY = e.clientY;
        const width = Math.abs(currentX - startX);
        const height = Math.abs(currentY - startY);
        const left = Math.min(startX, currentX);
        const top = Math.min(startY, currentY);

        selectionBox.style.left = `${left}px`;
        selectionBox.style.top = `${top}px`;
        selectionBox.style.width = `${width}px`;
        selectionBox.style.height = `${height}px`;
      };

      const handlePointerUp = (e) => {
        if (controls.contains(e.target)) return;
        e.preventDefault(); e.stopPropagation();

        if (isSelecting) {
          overlay.releasePointerCapture(e.pointerId);
          isSelecting = false;

          const endX = e.clientX;
          const endY = e.clientY;
          const width = Math.abs(endX - startX);
          const height = Math.abs(endY - startY);
          const left = Math.min(startX, endX);
          const top = Math.min(startY, endY);

          if (width > 10 && height > 10) {
            currentSelection = { x: left, y: top, w: width, h: height };
            updateUI('selected');
          } else {
            selectionBox.style.display = 'none';
            updateUI('frozen');
          }
        }
      };

      // --- CLEANUP ---
      const cleanupElements = () => {
        if (overlay) overlay.remove();
        if (video) video.remove();
        if (frozenImg) frozenImg.remove();
        if (stream) stream.getTracks().forEach(t => t.stop());
        document.removeEventListener('keydown', handleKeyDown);
        setIsSelectingRegion(false);
      };

      cleanup = () => {
        cleanupElements();
        window.removeEventListener('click', preventEvent, { capture: true });
        setIsCapturing(false);
        setIsHidden(false); // <--- SHOW MODAL AGAIN ON CANCEL
      };

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            cleanup();
        }
      };

      overlay.addEventListener('pointerdown', handlePointerDown);
      overlay.addEventListener('pointermove', handlePointerMove);
      overlay.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('keydown', handleKeyDown, true);

      setIsSelectingRegion(true);

    } catch (error) {
      console.error("Capture error:", error);
      if (cleanup) cleanup();
      window.removeEventListener('click', preventEvent, { capture: true });
      setIsCapturing(false);
      setIsHidden(false); // <--- SHOW MODAL AGAIN ON ERROR
    }
  };


  const handleAddToProblemList = () => {
    if (activeTab === 'icd10') {
      if (extractedCodes.length === 0) {
        toast.error("No codes to add");
        return;
      }

      if (onExtractComplete) {
        onExtractComplete(extractedCodes);
      }

      toast.success(`Added ${extractedCodes.length} codes to problem list`);
    } else {
      if (extractedMedications.length === 0) {
        toast.error("No validated medications to add");
        return;
      }

      if (onExtractComplete) {
        onExtractComplete(extractedMedications);
      }

      toast.success(`Added ${extractedMedications.length} medications to medication list`);
    }
    handleClose();
  };

  const handleClose = () => {
    if (isProcessing) {
      toast.error("Please wait for processing to complete");
      return;
    }

    if (workerRef.current) {
      workerRef.current.terminate().catch(console.error);
      workerRef.current = null;
    }

    // Clean up screen capture if active
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current = null;
    }

    setFiles([]);
    setExtractedCodes([]);
    setExtractedMedications([]);
    setProgress(0);
    setStatus("");
    setIsComplete(false);
    setIsProcessing(false);
    setIsCapturing(false);
    setIsSelectingRegion(false);
    setActiveTab(mode === 'medication' ? 'medication' : 'icd10');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        style={{ 
          opacity: isHidden ? 0 : 1, 
          pointerEvents: isHidden ? 'none' : 'auto' 
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileImage className="h-5 w-5 text-blue-500" />
            Extract from Images
          </DialogTitle>
          <DialogDescription>
            Upload HIPAA-compliant screenshots to extract ICD-10 codes or medications. All
            processing is done locally on your device.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="icd10" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              ICD-10 Codes
            </TabsTrigger>
            <TabsTrigger value="medication" className="flex items-center gap-2">
              <Pill className="h-4 w-4" />
              Medications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="icd10" className="space-y-4 mt-4">
            <div className="space-y-4">
          {/* HIPAA Compliance Notice */}
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>HIPAA Compliant:</strong> All OCR processing runs entirely
              on your device. No images or data are sent to external servers.
            </AlertDescription>
          </Alert>

          {/* File Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            onClick={() => !isProcessing && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={isProcessing}
              className="hidden"
            />
            <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-sm font-medium text-gray-700 mb-1">
              Drag & drop images here, or click to browse
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG, TIFF, BMP, WEBP, GIF up to 20MB each
            </p>
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">
                Selected Files ({files.length})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {file.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={isProcessing}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress */}
          {isProcessing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{status}</span>
                <span className="text-gray-600 font-medium">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress value={progress} />
            </div>
          )}

              {/* Results */}
              {isComplete && activeTab === 'icd10' && extractedCodes.length > 0 && (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Successfully extracted{" "}
                      <strong>{extractedCodes.length}</strong> ICD-10 codes
                    </AlertDescription>
                  </Alert>

                  <div className="max-h-60 overflow-y-auto border rounded-lg p-4">
                    <div className="space-y-2">
                      {extractedCodes.map((code, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-2 p-2 rounded ${
                            code.isValid === false
                              ? "bg-red-50 border border-red-200"
                              : "bg-gray-50"
                          }`}
                        >
                          <span className="font-mono text-sm font-medium text-blue-600">
                            {code.code}
                          </span>
                          <span
                            className={`text-sm flex-1 ${
                              code.isValid === false
                                ? "text-red-600 italic"
                                : "text-gray-700"
                            }`}
                          >
                            {code.description || "No description"}
                          </span>
                          {code.isValid === false && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                              Invalid
                            </span>
                          )}
                          {code.hcc && code.isValid !== false && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              HCC
                            </span>
                          )}
                          {code.isValidated && code.isValid && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="medication" className="space-y-4 mt-4">
            <div className="space-y-4">
              {/* HIPAA Compliance Notice */}
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <strong>HIPAA Compliant:</strong> All OCR processing runs entirely
                  on your device. No images or data are sent to external servers.
                </AlertDescription>
              </Alert>

              {/* File Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => !isProcessing && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  disabled={isProcessing}
                  className="hidden"
                />
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Drag & drop images here, or click to browse
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, TIFF, BMP, WEBP, GIF up to 20MB each
                </p>
              </div>

              {/* Selected Files */}
              {files.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">
                    Selected Files ({files.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {file.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          disabled={isProcessing}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress */}
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{status}</span>
                    <span className="text-gray-600 font-medium">
                      {Math.round(progress)}%
                    </span>
                  </div>
                  <Progress value={progress} />
                </div>
              )}

              {/* Results */}
              {isComplete && activeTab === 'medication' && extractedMedications.length > 0 && (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Successfully extracted{" "}
                      <strong>{extractedMedications.length}</strong> medications
                      {extractedMedications.filter(m => m.isValidated && m.isValid).length > 0 && (
                        <> ({extractedMedications.filter(m => m.isValidated && m.isValid).length} validated, {extractedMedications.filter(m => !m.isValidated || !m.isValid).length} unverified)</>
                      )}
                    </AlertDescription>
                  </Alert>

                  <div className="max-h-60 overflow-y-auto border rounded-lg p-4">
                    <div className="space-y-2">
                      {extractedMedications.map((medication, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-2 p-2 rounded ${
                            medication.isValidated && medication.isValid
                              ? "bg-green-50 border border-green-200"
                              : medication.isValidated && !medication.isValid
                              ? "bg-orange-50 border border-orange-200"
                              : "bg-gray-50 border border-gray-200"
                          }`}
                        >
                          <span className="text-sm font-medium text-purple-600">
                            {medication.medicationName}
                          </span>
                          {medication.companyName && (
                            <span className="text-xs text-gray-500">
                              ({medication.companyName})
                            </span>
                          )}
                          {medication.medicationType && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {medication.medicationType}
                            </span>
                          )}
                          {medication.dosage && (
                            <span className="text-xs text-gray-600">
                              {medication.dosage}
                            </span>
                          )}
                          {medication.status && medication.status !== 'taking' && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              medication.status === 'not-taking' 
                                ? 'bg-red-100 text-red-800' 
                                : medication.status === 'discontinued'
                                ? 'bg-gray-100 text-gray-800'
                                : ''
                            }`}>
                              {medication.status === 'not-taking' ? 'Not Taking' : medication.status === 'discontinued' ? 'Discontinued' : medication.status}
                            </span>
                          )}
                          {medication.isValidated && medication.isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600 ml-auto" title="Validated" />
                          ) : medication.isValidated && !medication.isValid ? (
                            <AlertCircle className="h-4 w-4 text-orange-600 ml-auto" title="Not in database" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-gray-600 ml-auto" title="Unverified" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing || isCapturing}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {isComplete && (
              (activeTab === 'icd10' && extractedCodes.length > 0) ||
              (activeTab === 'medication' && extractedMedications.length > 0)
            ) && (
              <Button
                onClick={handleAddToProblemList}
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {activeTab === 'icd10' ? 'Add to Problem List' : 'Add to Medication List'}
              </Button>
            )}
            {!isProcessing && files.length > 0 && !isComplete && (
              <Button
                onClick={handleExtract}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <FileImage className="h-4 w-4 mr-2" />
                Extract {activeTab === 'icd10' ? 'ICD-10 Codes' : 'Medications'}
              </Button>
            )}
            {isProcessing && (
              <Button disabled>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </Button>
            )}
          </div>
        </div>

        {/* Screenshot Capture Button */}
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            onClick={handleScreenshotCapture}
            disabled={isProcessing || isCapturing}
            className="w-full"
          >
            {isCapturing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Capturing...
              </>
            ) : (
              <>
                <Camera className="h-4 w-4 mr-2" />
                Capture Screenshot
              </>
            )}
          </Button>
          {isSelectingRegion && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              Select a region on your screen to capture
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OCRModal;
